import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { generateReminderDateOptions, horizonMonthsForFrequency, REMINDER_TYPES, ReminderConstants, TIME_ZONES } from "../../../constants/reminderConstants";
import { CALENDAR_DATE_FORMAT, getToastMessageForSubscriptionLimitReached } from "../../../constants/constants";
import { InputTextarea } from "primereact/inputtextarea";
import { FilterMatchMode } from "primereact/api";
import { set } from "mobx";
import { time } from "console";
import { Checkbox } from "primereact/checkbox";
import { RouterAddress } from "../../../constants/RouterAddress";
import { all } from "axios";
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format } from 'date-fns/format';
import { parse } from 'date-fns/parse';
import { startOfWeek } from 'date-fns/startOfWeek';
import { getDay } from 'date-fns/getDay';
import { enUS } from 'date-fns/locale/en-US';

import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appFormSectionStyle,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appSectionCardStyle,
  appWideDialogStyle,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import SubscribeForUnlimitedAccessDialog from "../../../components/general/SubscribeForUnlimitedAccessDialog";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

const UserReminder = () => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const navigate = useNavigate();
  const isMobile = useResponsive().isMobile;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const { completeStep, isStepActive } = useTour();

  const [errorMessageNewReminder, setErrorMessageNewReminder] = useState<string | null>(null);
  const [errorMessageEditReminder, setErrorMessageEditReminder] = useState<string | null>(null);

  const [newReminderProgress, setNewReminderProgress] = useState<boolean>(false);
  const [selectedPredefinedReminder, setSelectedPredefinedReminder] = useState<any>(null);
  const [startDateLabel, setStartDateLabel] = useState<string>("");
  const [dueDateLabel, setDueDateLabel] = useState<string>("");

  const [loadExistingRemindersProgress, setLoadExistingRemindersProgress] = useState<boolean>(false);

  const [allReminders, setAllReminders] = useState<any[] | null>(null);
  const [showNewCustomReminderDialog, setShowNewCustomReminderDialog] = useState<boolean>(false);
  const [showReminderLimitDialog, setShowReminderLimitDialog] = useState<boolean>(false);

  const [possibleReminderDates, setPossibleReminderDates] = useState<any[] | null>(null);
  const [recommendedFrequency, setRecommendedFrequency] = useState<string>("");
  const [recommendedEarlyReminder, setRecommendedEarlyReminder] = useState<string>("");

  const [showUsePredefinedPopUp, setShowUsePredefinedPopUp] = useState<boolean>(false);
  const [showEditReminderPopUp, setShowEditReminderPopUp] = useState<boolean>(false);

  const [showUpcomingRemindersPopUp, setShowUpcomingRemindersPopUp] = useState<boolean>(false);
  const [upcomingReminders, setUpcomingReminders] = useState<any[] | null>(null);

  const [showAllRemindersPopUp, setShowAllRemindersPopUp] = useState<boolean>(false);
  const [allRemindersByDate, setAllRemindersByDate] = useState<any[] | null>(null);

  const DEFAULT_UPCOMING_MONTHS_FILTER = 3;
  const UPCOMING_MONTH_DROPDOWN_OPTIONS = [
    { label: "Next 1 month", value: 1 },
    { label: "Next 3 months", value: 3 },
    { label: "Next 6 months", value: 6 },
    { label: "Next 12 months", value: 12 }];

  const [selectedUpcomingReminderFilter, setSelectedUpcomingReminderFilter] = useState<number>(DEFAULT_UPCOMING_MONTHS_FILTER);

  const defaultValues = {
    reminder: {
      reminderId: "",
      referenceId: "",
      role: "",
      priority: "",
      title: "",
      description: "",
      startDate: new Date(),
      startDateLabel: "",
      dueDate: new Date() || undefined,
      dueDateLabel: "",
      timezone: "America/Los_Angeles",
      frequency: null,
      earlyWeeks: "",
      setSampleReminder: false,
      active: true,
    }
  }

  useEffect(() => {
    const fetchReminders = async () => {
      setLoadExistingRemindersProgress(true);
      try {
        await backendAPIStore.init();
        const existingReminders = await backendAPIStore.listRemindersForCurrentUser();
        setAllReminders(existingReminders);
      } catch (error: any) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load your reminders. Please try again later.' });
      }
      finally {
        setLoadExistingRemindersProgress(false);
      }
    };

    fetchReminders();
  }, [backendAPIStore, toast])

  useEffect(() => {
    const shouldOpenReminderEditor = isStepActive("reminder-editor") || isStepActive("reminder-save");
    if (shouldOpenReminderEditor && !showNewCustomReminderDialog) {
      onNewCustomReminderClick();
    }
  }, [showNewCustomReminderDialog, isStepActive]);

  const { control, formState: { errors }, handleSubmit, reset, watch, getValues, setValue } = useForm({ defaultValues });

  const onSubmitReminderClick = async (data: any) => {
    setErrorMessageNewReminder(null);
    setNewReminderProgress(true);
    try {
      await backendAPIStore.init();
      await backendAPIStore.postReminder(data.reminder);
      if (isStepActive("reminder-save")) {
        completeStep("reminder-save");
      }
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Your reminder has been saved successfully!' });
      setShowUsePredefinedPopUp(false);
      setShowNewCustomReminderDialog(false);

      const existingReminders = await backendAPIStore.listRemindersForCurrentUser();
      setAllReminders(existingReminders);
    } catch (error: any) {
      setErrorMessageNewReminder(error.message);
      if (error?.code === "SUBSCRIPTION_LIMIT_REACHED" || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED") {
        setShowReminderLimitDialog(true);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setNewReminderProgress(false);
    }
  };

  const onSubmitEditReminderClick = async (data: any) => {
    setErrorMessageEditReminder(null);
    try {
      await backendAPIStore.updateReminder(data.reminder);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Your reminder has been saved successfully!' });
      setShowEditReminderPopUp(false);

      const existingReminders = await backendAPIStore.listRemindersForCurrentUser();
      setAllReminders(existingReminders);
    } catch (error: any) {
      setErrorMessageEditReminder(error.message);
    } finally {
      setNewReminderProgress(false);
    }
  };

  const titleTemplate = (title, count) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900" style={{ textTransform: "capitalize" }}>{title} ({count})</span>
      </div>
    );
  };

  const reminderCalendarProps = {
    showButtonBar: true,
    showIcon: true,
    dateFormat: CALENDAR_DATE_FORMAT,
    monthNavigator: true,
    yearNavigator: true,
    yearRange: "2020:2045",
    touchUI: isMobile,
  } as const;

  const getPrioritySeverity = (priority: string) => {
    const normalized = (priority || "").toLowerCase();
    if (normalized.includes("high") || normalized.includes("urgent")) return "danger";
    if (normalized.includes("medium")) return "warning";
    return "info";
  };

  const actionBodyTemplate = (rowData) => (
    <div className="flex align-items-center gap-2">
      <Button icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => onEditReminderClick(rowData)} data-telemetry-action="reminder_edit" />
      <Button icon="pi pi-trash" severity="danger" outlined rounded onClick={() => onDeleteReminderClick(rowData)} data-telemetry-action="reminder_delete" />
    </div>
  );

  const priorityBodyTemplate = (rowData) => (
    <Tag value={rowData.priority || "N/A"} severity={getPrioritySeverity(rowData.priority)} style={{ borderRadius: "999px", padding: "0.35rem 0.75rem" }} />
  );

  const roleBodyTemplate = (rowData) => (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.35rem 0.75rem",
      borderRadius: "999px",
      background: "#eff6ff",
      color: "#185DA2",
      fontWeight: 600,
      textTransform: "capitalize",
    }}>
      {rowData.role || "N/A"}
    </span>
  );

  const frequencyBodyTemplate = (rowData, field = "frequency") => (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.35rem 0.75rem",
      borderRadius: "999px",
      background: "#f8fafc",
      border: "1px solid #dbeafe",
      color: "#334155",
      fontWeight: 600,
    }}>
      {rowData[field] || "N/A"}
    </span>
  );

  const descriptionBodyTemplate = (rowData) => (
    <div style={{ maxWidth: "360px", color: "#475569", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
      {rowData.description || "N/A"}
    </div>
  );

  const renderTableBanner = (title: string, subtitle: string) => (
    <div style={{ ...appAccentPanelStyle, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, marginBottom: 0 }}>
      <div style={{ fontSize: "1rem", fontWeight: 700, color: "#9a3412" }}>{title}</div>
      <div className="mt-2" style={appMutedTextStyle}>{subtitle}</div>
    </div>
  );

  const onNewCustomReminderClick = () => {
    reset({
      reminder: {
        role: "",
        priority: "",
        title: "",
        description: "",
        startDate: new Date(),
        startDateLabel: "",
        dueDate: new Date() || undefined,
        dueDateLabel: "",
        frequency: null,
        earlyWeeks: "",
        active: true,
        setSampleReminder: false,
        timezone: TIME_ZONES[0],
      }
    });
    setPossibleReminderDates(null);
    setStartDateLabel("Start Date");
    setDueDateLabel("Due Date");
    setShowNewCustomReminderDialog(true);
  }

  const onUpcomingRemindersClick = async (month: number) => {
    try {
      const response = await backendAPIStore.listUpcomingRemindersForCurrentUser(month);
      setUpcomingReminders(response);
      setShowUpcomingRemindersPopUp(true);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load your upcoming reminders. Please try again later.' });
      setShowUpcomingRemindersPopUp(false);
    }
  };

  const onAllRemindersByDateClick = async () => {
    try {
      const responseAllRemindersByDate = await backendAPIStore.listAllPastRemindersForCurrentUser();
      setAllRemindersByDate(responseAllRemindersByDate);
      setShowAllRemindersPopUp(true);
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load your reminders. Please try again later.' });
      setShowAllRemindersPopUp(false);
    }
  };

  const headerTemplate = () => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-center gap-2 m-2" data-tour="reminder-actions">
        <Button
          label="New Reminder"
          icon="pi pi-plus"
          className="p-button-sm"
          severity="warning"
          outlined
          rounded
          data-tour="reminder-new"
          data-telemetry-action="reminder_open_create_dialog"
          onClick={() => {
            if (isStepActive("reminder-new-cta")) {
              completeStep("reminder-new-cta");
            }
            onNewCustomReminderClick();
          }}
        />
        <Button label="Upcoming" className="p-button-sm" severity="warning" outlined rounded onClick={() => onUpcomingRemindersClick(selectedUpcomingReminderFilter)} data-telemetry-action="reminder_open_upcoming" />
        <Button label="History" className="p-button-sm" severity="warning" outlined rounded onClick={onAllRemindersByDateClick} data-telemetry-action="reminder_open_history" />
      </div>
    );
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  // Commented out DueDate: Currently we are not using dueDate for calculating reminder dates and showing in UI, 
  // but we can easily add it back if needed in the future. We just need to uncomment the related code in showReminderDates function 
  // and make sure to pass dueDate when calling generateReminderDateOptions.
  const showReminderDates = () => {
    let reminderData = getValues("reminder");

    let startDate = new Date(reminderData.startDate);
    let dueDate = reminderData.dueDate ? new Date(reminderData.dueDate) : undefined;
    let earlyWeeks = ReminderConstants.convertEarlyReminderLabelToWeeks(reminderData.earlyWeeks);
    let frequency = reminderData.frequency;

    if (earlyWeeks && frequency) {
      const reminderDates = generateReminderDateOptions({
        startDate: startDate ? new Date(startDate) : new Date(),
        dueDate: undefined, // dueDate ? new Date(dueDate) : undefined,
        frequency: frequency!,
        earlyReminderOptions: [earlyWeeks],
        horizonMonths: horizonMonthsForFrequency(frequency!), // dueDate ? undefined : horizonMonthsForFrequency(frequency!),
        lookbackMonths: 0,
        futureOnly: true,
      });
      setPossibleReminderDates(reminderDates);
    }
  };

  const onDeleteReminderClick = (rowData) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete reminder",
        "This will remove the selected reminder and its future schedule entries from your list.",
      ),
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'No, Cancel',
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deleteReminder(rowData.reminderId);
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Your reminder has been deleted.' });
          const existingReminders = await backendAPIStore.listRemindersForCurrentUser();

          setAllReminders(existingReminders);
        } catch (error: any) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete the reminder. Please try again later.' });
        }
      }
    });
  };

  const onEditReminderClick = (rowData) => {
    setValue("reminder.reminderId", rowData.reminderId);
    setValue("reminder.referenceId", rowData.referenceId);
    setValue("reminder.role", rowData.role);
    setValue("reminder.priority", rowData.priority);
    setValue("reminder.title", rowData.title);
    setValue("reminder.description", rowData.description);
    setValue("reminder.startDate", new Date(rowData.startDate));
    setValue("reminder.startDateLabel", rowData.startDateLabel);
    if (rowData.dueDate) {
      setValue("reminder.dueDate", new Date(rowData.dueDate));
    }
    setValue("reminder.dueDateLabel", rowData.dueDateLabel);
    setValue("reminder.frequency", rowData.frequency);
    setValue("reminder.earlyWeeks", rowData.earlyWeeks);
    setValue("reminder.active", rowData.active);
    setValue("reminder.timezone", rowData.timezone);
    setValue("reminder.setSampleReminder", false);

    setPossibleReminderDates(null);
    setSelectedPredefinedReminder(rowData);
    setRecommendedFrequency(rowData.frequency);
    setRecommendedEarlyReminder(rowData.earlyWeeks);
    setStartDateLabel(rowData.startDateLabel);
    setDueDateLabel(rowData.dueDateLabel);

    setShowEditReminderPopUp(true);
    showReminderDates();
  };

  const showUserNotSubscribedMessage = () => {
    toast.current?.show({
      severity: 'info',
      summary: 'Subscription Required',
      detail:
        <div>
          Please subscribe to our service to access all features.
          <br />
          <Button label="View Plans" className="p-button-sm mt-2" severity="warning" rounded onClick={() => navigate(RouterAddress.BILLING)} data-telemetry-action="reminder_view_plans" />
        </div>, life: 5000
    });
  };

  const onSelectTemplateClick = (rowData) => {
    reset({
      reminder: {
        role: rowData.role,
        priority: rowData.priority,
        title: rowData.title + " - <REPLACE WITH SPECIFIC NAME/DETAILS>",
        description: rowData.description,
        startDate: new Date(),
        startDateLabel: rowData.startReminderDateLabel,
        dueDate: undefined,
        dueDateLabel: rowData.dueDateReminderLabel,
        active: true,
        frequency: rowData.recommendedFrequency,
        earlyWeeks: rowData.recommendedEarlyReminder,
        timezone: TIME_ZONES[0],
        setSampleReminder: false,
      }
    });

    setPossibleReminderDates(null);
    setSelectedPredefinedReminder(rowData);
    setRecommendedFrequency(rowData.recommendedFrequency);
    setRecommendedEarlyReminder(rowData.recommendedEarlyReminder);
    setStartDateLabel(rowData.startReminderDateLabel);
    setDueDateLabel(rowData.dueDateReminderLabel);

    setShowUsePredefinedPopUp(true);
    showReminderDates();
  };

  const roleFilterTemplate = (options) => {
    return <Dropdown value={options.value} options={ReminderConstants.ROLE_OPTIONS} onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Select role" className="p-column-filter" />;
  };

  const locales = {
    'en-US': enUS,
  }

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  })

  const events = [
    {
      title: 'Doctor Appointment',
      start: new Date(2026, 2, 18, 10, 0),
      end: new Date(2026, 2, 18, 11, 0),
    },
    {
      title: 'Medication Reminder',
      start: new Date(2026, 2, 18, 14, 0),
      end: new Date(2026, 2, 18, 14, 30),
    },
  ]

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component">
      <Toast ref={toast} position="top-right" baseZIndex={10000} />
      <SubscribeForUnlimitedAccessDialog
        visible={showReminderLimitDialog}
        onHide={() => setShowReminderLimitDialog(false)}
        title="More Reminder Access Needed"
        message="You have used the free reminder limit on your account. Subscribe to keep adding more reminders."
      />
      {/* 
      <div style={{ height: '600px' }}>
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
        />
      </div> */}
      <Card title={titleTemplate("My Reminders", allReminders?.length)} header={headerTemplate} style={appSectionCardStyle} data-tour="reminder-overview">
        {
          allReminders && allReminders.length > 0 && !isMobile && (
            <div style={appDataTableWrapperStyle}>
              {renderTableBanner("Reminder Dashboard", "Browse active reminders with a clearer priority, role, and schedule view.")}
              <DataTable value={allReminders!} emptyMessage="No reminders yet." loading={loadExistingRemindersProgress} showGridlines
                paginator rows={5} stripedRows sortMode="multiple" filterDisplay="row">
                <Column header="Actions" body={actionBodyTemplate}></Column>
                <Column header="Priority" field="priority" body={priorityBodyTemplate} sortable></Column>
                <Column header="Role" field="role" body={roleBodyTemplate} sortable filter filterMenuStyle={{ width: '5rem' }} filterElement={roleFilterTemplate}></Column>
                <Column header="Title" field="title" sortable filter filterMenuStyle={{ width: '5rem' }}></Column>
                <Column header="Description" field="description" body={descriptionBodyTemplate}></Column>
                <Column header="Frequency" field="frequency" body={(rowData) => frequencyBodyTemplate(rowData, "frequency")} sortable></Column>
                <Column header="Early Reminder" field="earlyWeeks" body={(rowData) => frequencyBodyTemplate(rowData, "earlyWeeks")} sortable></Column>
              </DataTable>
            </div>)
        }
        {
          allReminders && allReminders.length > 0 && isMobile && (
            allReminders.map((reminder) => (
              <Card key={reminder.reminderId} title={<h5>{reminder.title}</h5>} subTitle={`Priority: ${reminder.priority} | Role: ${reminder.role}`}
                className="mt-2" style={appInnerCardStyle}
                footer={
                  <div className="flex justify-content-end gap-2">
                    <Button icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => onEditReminderClick(reminder)} data-telemetry-action="reminder_edit_card"></Button>
                    <Button icon="pi pi-trash" severity="danger" outlined rounded onClick={() => onDeleteReminderClick(reminder)} data-telemetry-action="reminder_delete_card"></Button>
                  </div>
                }>
                <p style={{ whiteSpace: 'pre-wrap' }}>{reminder.description}</p>
                <p>Frequency: {reminder.frequency}</p>
                <p>Early Reminder: {reminder.earlyWeeks}</p>
              </Card>)
            ))
        }
      </Card>

      <Card title={titleTemplate("Reminder Templates", ReminderConstants.AFH_REMINDERS?.length)} style={appSectionCardStyle} data-tour="reminder-templates">
        {
          isMobile ? ReminderConstants.AFH_REMINDERS.map((template) => (
            <Card title={<h5>{template.title}</h5>} subTitle={`Priority: ${template.priority} | Role: ${template.role}`}
              className="mt-2" style={appInnerCardStyle}
              footer={
                <div className="flex justify-content-end gap-2">
                  <Button label="Use" severity="warning" outlined rounded onClick={() => onSelectTemplateClick(template)} />
                </div>
              }>
              <p style={{ whiteSpace: 'pre-wrap' }}>{template.description}</p>
              <p>Frequency: {template.recommendedFrequency}</p>
              <p>Early Reminder: {template.recommendedEarlyReminder}</p>
            </Card>)
          ) : (
            <div style={appDataTableWrapperStyle}>
              {renderTableBanner("Template Library", "Start from curated reminder templates and adapt them to your workflow.")}
              <DataTable value={ReminderConstants.AFH_REMINDERS!} stripedRows sortMode="multiple" paginator rows={5} showGridlines
                filterDisplay="row">
                <Column header="Actions" body={(rowData) => (
                  <Button label="Use" severity="warning" outlined rounded onClick={() => onSelectTemplateClick(rowData)} />
                )}></Column>
                <Column header="Priority" field="priority" body={priorityBodyTemplate} sortable></Column>
                <Column header="Role" field="role" body={roleBodyTemplate} sortable filter filterMenuStyle={{ width: '5rem' }} filterElement={roleFilterTemplate}></Column>
                <Column header="Title" field="title" sortable filter filterMenuStyle={{ width: '5rem' }} filterMatchMode="contains"></Column>
                <Column header="Description" field="description" body={descriptionBodyTemplate} sortable></Column>
                <Column header="Recommended Frequency" field="recommendedFrequency" body={(rowData) => frequencyBodyTemplate(rowData, "recommendedFrequency")} sortable></Column>
                <Column header="Recommended Early Reminder" field="recommendedEarlyReminder" body={(rowData) => frequencyBodyTemplate(rowData, "recommendedEarlyReminder")} sortable></Column>
              </DataTable>
            </div>
          )
        }
      </Card>

      <Dialog header={renderSurfaceHeader("Create Reminder From Template", "Customize the selected template before saving it to your schedule.", "pi pi-copy")} visible={showUsePredefinedPopUp} style={appWideDialogStyle} modal onHide={() => setShowUsePredefinedPopUp(false)}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        footer={
          <div className="justify-content-center align-items-center flex flex-wrap">
            <Button label="Close" severity="secondary" outlined rounded className="mt-2 p-button-outlined" onClick={() => setShowUsePredefinedPopUp(false)} />
            <Button type="submit" label="Create" severity="warning" rounded className="mt-2 p-button-outlined"
              loading={newReminderProgress} onClick={handleSubmit(onSubmitReminderClick)} data-telemetry-action="reminder_create_from_template" />
          </div>
        }>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Template-based reminder</div>
          <div className="mt-2" style={appMutedTextStyle}>Adjust the title, schedule, and lead time before creating the reminder.</div>
        </div>
        <form className="justify-content-center align-items-center flex flex-wrap">
          <div className="m-4" style={appFormSectionStyle}>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.title" className={classNames({ 'p-error': errors.reminder?.title })}>
                  <strong>Reminder Title </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.title" control={control} rules={{
                  required: 'Title is required.',
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Enter title" style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.title')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.description" className={classNames({ 'p-error': errors.reminder?.description })}>
                  <strong>Description </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.description" control={control} rules={{
                  required: 'Description is required.',
                }} render={({ field, fieldState }) => (
                  <InputTextarea id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Enter description"
                    rows={5} style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.description')}
            </div>
            {/* <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.dueDate" className={classNames({ 'p-error': errors.reminder?.dueDate })}>
                  <strong>{dueDateLabel} (Optional) </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.dueDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} minDate={new Date()} showButtonBar showIcon dateFormat={CALENDAR_DATE_FORMAT} />
                )} />
              </span>
              {getFormErrorMessage('reminder.dueDate')}
            </div> */}
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.startDate" className={classNames({ 'p-error': errors.reminder?.startDate })}>
                  <strong>{startDateLabel}</strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.startDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value} onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} {...reminderCalendarProps} />
                )} />
              </span>
              {getFormErrorMessage('reminder.startDate')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.frequency" className={classNames({ 'p-error': errors.reminder?.frequency })}>
                  <strong>Reminder Frequency: </strong>
                  <span><small>(How often this reminder repeats.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.frequency" control={control} rules={{
                  required: 'Frequency is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={ReminderConstants.REMINDER_FREQUENCY}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select frequency" />
                )} />
              </span>
              {getFormErrorMessage('reminder.frequency')}
            </div>
            <div className="field mt-1">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.earlyWeeks" className={classNames({ 'p-error': errors.reminder?.earlyWeeks })}>
                  <strong>How early you want your reminders before the due date: </strong>
                  <span><small>(This determines how many weeks in advance you will be reminded.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.earlyWeeks" control={control} rules={{
                  required: 'Early Reminder Weeks is required.',
                }} render={({ field, fieldState }) => (
                  <>
                    <Dropdown id={field.name} value={field.value} options={ReminderConstants.EARLY_REMINDER_WEEKS}
                      onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                      className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select early reminder weeks ..." />
                  </>
                )} />
              </span>
              {getFormErrorMessage('reminder.earlyWeeks')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.timezone" className={classNames({ 'p-error': errors.reminder?.timezone })}>
                  <strong>Time Zone: </strong>
                  <span><small>(Reminder delivery time zone.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.timezone" control={control} rules={{
                  required: 'Time Zone is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={TIME_ZONES}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select time zone" />
                )} />
              </span>
              {getFormErrorMessage('reminder.timezone')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.setSampleReminder" className={classNames({ 'p-error': errors.reminder?.setSampleReminder })}>
                  <strong>Send Test Reminder Email: </strong>
                  <span><small>(Sends one sample email after about 2 minutes.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.setSampleReminder" control={control} render={({ field, fieldState }) => (
                  <Checkbox id={field.name} checked={field.value} onChange={(e) => { field.onChange(e.checked); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
              </span>
              {getFormErrorMessage('reminder.setSampleReminder')}
            </div>
            {
              possibleReminderDates && possibleReminderDates.length > 0 && (
                <div className="mt-4">
                  <strong>Preview reminder schedule:</strong>
                  <ul>
                    {possibleReminderDates.map((dateOption, index) => (
                      <li key={index}>
                        Reminder date: <strong>{new Date(dateOption.reminderDateISO).toLocaleDateString()}</strong> {dueDateLabel && <>before due date <strong>{new Date(dateOption.dueDateISO).toLocaleDateString()}</strong></>}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
          </div>
          <div className="p-error">{errorMessageNewReminder}</div>
        </form >
      </Dialog >

      <Dialog header={renderSurfaceHeader("Create Reminder", "Build a custom reminder with your own title, schedule, and timezone.", "pi pi-plus-circle")} visible={showNewCustomReminderDialog} style={appWideDialogStyle} modal
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        onHide={() => setShowNewCustomReminderDialog(false)}
        footer={
          <div className="justify-content-center align-items-center flex flex-wrap">
            <Button label="Close" severity="secondary" outlined rounded className="mt-2 p-button-outlined" onClick={() => setShowNewCustomReminderDialog(false)} />
            <Button type="submit" label="Create" severity="warning" rounded className="mt-2 p-button-outlined"
              data-tour="reminder-save"
              loading={newReminderProgress} onClick={handleSubmit(onSubmitReminderClick)} data-telemetry-action="reminder_create_custom" />
          </div>
        }>
        <div style={appAccentPanelStyle} className="mb-3" data-tour="reminder-create-dialog">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Custom reminder</div>
          <div className="mt-2" style={appMutedTextStyle}>Choose the recurrence, early reminder timing, and whether to send yourself a sample email.</div>
        </div>
        <form className="justify-content-center align-items-center flex flex-wrap">
          <div className="m-4" style={appFormSectionStyle}>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.title" className={classNames({ 'p-error': errors.reminder?.title })}>
                  <strong>Reminder Title </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.title" control={control} rules={{
                  required: 'Title is required.',
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Title ..." style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.title')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.description" className={classNames({ 'p-error': errors.reminder?.description })}>
                  <strong>Description </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.description" control={control} rules={{
                  required: 'Description is required.',
                }} render={({ field, fieldState }) => (
                  <InputTextarea id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Description ..."
                    rows={5} style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.description')}
            </div>
            {/* <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.dueDate" className={classNames({ 'p-error': errors.reminder?.dueDate })}>
                  <strong>{dueDateLabel} (Optional) </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.dueDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} minDate={new Date()} showButtonBar showIcon dateFormat={CALENDAR_DATE_FORMAT} />
                )} />
              </span>
              {getFormErrorMessage('reminder.dueDate')}
            </div> */}
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.startDate" className={classNames({ 'p-error': errors.reminder?.startDate })}>
                  <strong>{startDateLabel}</strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.startDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value} onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} {...reminderCalendarProps} />
                )} />
              </span>
              {getFormErrorMessage('reminder.startDate')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.frequency" className={classNames({ 'p-error': errors.reminder?.frequency })}>
                  <strong>Frequency of this reminder: </strong>
                  <span><small>(This determines how often you will be reminded.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.frequency" control={control} rules={{
                  required: 'Frequency is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={ReminderConstants.REMINDER_FREQUENCY}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select frequency ..." />
                )} />
              </span>
              {getFormErrorMessage('reminder.frequency')}
            </div>
            <div className="field mt-1">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.earlyWeeks" className={classNames({ 'p-error': errors.reminder?.earlyWeeks })}>
                  <strong>How early you want your reminders before the due date: </strong>
                  <span><small>(This determines how many weeks in advance you will be reminded.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.earlyWeeks" control={control} rules={{
                  required: 'Early Reminder Weeks is required.',
                }} render={({ field, fieldState }) => (
                  <>
                    <Dropdown id={field.name} value={field.value} options={ReminderConstants.EARLY_REMINDER_WEEKS}
                      onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                      className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select early reminder weeks ..." />
                  </>
                )} />
              </span>
              {getFormErrorMessage('reminder.earlyWeeks')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.timezone" className={classNames({ 'p-error': errors.reminder?.timezone })}>
                  <strong>Time Zone: </strong>
                  <span><small>(This determines the time zone for your reminders.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.timezone" control={control} rules={{
                  required: 'Time Zone is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={TIME_ZONES}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select time zone ..." />
                )} />
              </span>
              {getFormErrorMessage('reminder.timezone')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.setSampleReminder" className={classNames({ 'p-error': errors.reminder?.setSampleReminder })}>
                  <strong>Receive Sample Reminder Email: </strong>
                  <span><small>(You will receive a one-time sample reminder email after 2 minutes.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.setSampleReminder" control={control} render={({ field, fieldState }) => (
                  <Checkbox id={field.name} checked={field.value} onChange={(e) => { field.onChange(e.checked); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
              </span>
              {getFormErrorMessage('reminder.setSampleReminder')}
            </div>
            {
              possibleReminderDates && possibleReminderDates.length > 0 && (
                <div className="mt-4">
                  <strong>Expected upcoming dates based on selected frequency and early reminder preferences:</strong>
                  <ul>
                    {possibleReminderDates.map((dateOption, index) => (
                      <li key={index}>
                        You will be reminded on <strong>{new Date(dateOption.reminderDateISO).toLocaleDateString()}</strong> {dueDateLabel && <>before the due date of <strong>{new Date(dateOption.dueDateISO).toLocaleDateString()}</strong></>}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
          </div>
          <div className="p-error">{errorMessageNewReminder}</div>
        </form >
      </Dialog >

      <Dialog header={renderSurfaceHeader("Update Reminder", "Modify the reminder details, schedule, and notification timing.", "pi pi-pencil")} visible={showEditReminderPopUp} style={appWideDialogStyle} modal onHide={() => setShowEditReminderPopUp(false)}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        footer={
          <div className="justify-content-center align-items-center flex flex-wrap">
            <Button label="Close" severity="secondary" outlined rounded className="mt-2 p-button-outlined" onClick={() => setShowEditReminderPopUp(false)} />
            <Button type="submit" label="Update" severity="warning" rounded className="mt-2 p-button-outlined"
              loading={newReminderProgress} onClick={handleSubmit(onSubmitEditReminderClick)} data-telemetry-action="reminder_update" />
          </div>
        }>
        <div style={appAccentPanelStyle} className="mb-3">
          <div style={{ fontWeight: 700, color: "#9a3412" }}>Reminder editor</div>
          <div className="mt-2" style={appMutedTextStyle}>Update reminder content, frequency, and timezone while previewing the next expected dates.</div>
        </div>
        <form className="justify-content-center align-items-center flex flex-wrap">
          <div className="m-4" style={appFormSectionStyle}>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.title" className={classNames({ 'p-error': errors.reminder?.title })}>
                  <strong>Reminder Title </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.title" control={control} rules={{
                  required: 'Title is required.',
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Title ..." style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.title')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.description" className={classNames({ 'p-error': errors.reminder?.description })}>
                  <strong>Description </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.description" control={control} rules={{
                  required: 'Description is required.',
                }} render={({ field, fieldState }) => (
                  <InputTextarea id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Description ..."
                    rows={5} style={{ width: "100%" }} />
                )} />
              </span>
              {getFormErrorMessage('reminder.description')}
            </div>
            {/* <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.dueDate" className={classNames({ 'p-error': errors.reminder?.dueDate })}>
                  <strong>{dueDateLabel} (Optional) </strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.dueDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} minDate={new Date()} showButtonBar showIcon dateFormat={CALENDAR_DATE_FORMAT} />
                )} />
              </span>
              {getFormErrorMessage('reminder.dueDate')}
            </div> */}
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.startDate" className={classNames({ 'p-error': errors.reminder?.startDate })}>
                  <strong>{startDateLabel}</strong>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.startDate" control={control} render={({ field, fieldState }) => (
                  <Calendar id={field.name} value={field.value} onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} {...reminderCalendarProps} />
                )} />
              </span>
              {getFormErrorMessage('reminder.startDate')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.frequency" className={classNames({ 'p-error': errors.reminder?.frequency })}>
                  <strong>Frequency of this reminder: </strong>
                  <span><small>(This determines how often you will be reminded.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.frequency" control={control} rules={{
                  required: 'Frequency is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={ReminderConstants.REMINDER_FREQUENCY}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select frequency ..." />
                )} />
              </span>
              {getFormErrorMessage('reminder.frequency')}
            </div>
            <div className="field mt-1">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.earlyWeeks" className={classNames({ 'p-error': errors.reminder?.earlyWeeks })}>
                  <strong>How early you want your reminders before the due date: </strong>
                  <span><small>(This determines how many weeks in advance you will be reminded.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.earlyWeeks" control={control} rules={{
                  required: 'Early Reminder Weeks is required.',
                }} render={({ field, fieldState }) => (
                  <>
                    <Dropdown id={field.name} value={field.value} options={ReminderConstants.EARLY_REMINDER_WEEKS}
                      onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                      className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select early reminder weeks ..." />
                  </>
                )} />
              </span>
              {getFormErrorMessage('reminder.earlyWeeks')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.timezone" className={classNames({ 'p-error': errors.reminder?.timezone })}>
                  <strong>Time Zone: </strong>
                  <span><small>(This determines the time zone for your reminders.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.timezone" control={control} rules={{
                  required: 'Time Zone is required.',
                }} render={({ field, fieldState }) => (
                  <Dropdown id={field.name} value={field.value} options={TIME_ZONES}
                    onChange={(e) => { field.onChange(e.value); showReminderDates(); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Select time zone ..." />
                )} />
              </span>
              {getFormErrorMessage('reminder.timezone')}
            </div>
            <div className="field mt-4">
              <span className="flex align-items-center mb-2">
                <label htmlFor="reminder.setSampleReminder" className={classNames({ 'p-error': errors.reminder?.setSampleReminder })}>
                  <strong>Receive Sample Reminder Email: </strong>
                  <span><small>(You will receive a one-time sample reminder email after 2 minutes.)</small></span>
                </label>
              </span>
              <span className="p-float-label">
                <Controller name="reminder.setSampleReminder" control={control} render={({ field, fieldState }) => (
                  <Checkbox id={field.name} checked={field.value} onChange={(e) => { field.onChange(e.checked); }}
                    className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
              </span>
              {getFormErrorMessage('reminder.setSampleReminder')}
            </div>
            {
              possibleReminderDates && possibleReminderDates.length > 0 && (
                <div className="mt-4">
                  <strong>Expected upcoming dates based on selected frequency and early reminder preferences:</strong>
                  <ul>
                    {possibleReminderDates.map((dateOption, index) => (
                      <li key={index}>
                        You will be reminded on <strong>{new Date(dateOption.reminderDateISO).toLocaleDateString()}</strong> {dueDateLabel && <>before the due date of <strong>{new Date(dateOption.dueDateISO).toLocaleDateString()}</strong></>}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
          </div>
          <div className="p-error">{errorMessageEditReminder}</div>
        </form >
      </Dialog >

      <Dialog header={renderSurfaceHeader("Upcoming Reminders", "Review reminders scheduled in the next selected time range.", "pi pi-calendar")} visible={showUpcomingRemindersPopUp} style={appWideDialogStyle} modal onHide={() => setShowUpcomingRemindersPopUp(false)}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        footer={
          <div className="justify-content-center align-items-center flex flex-wrap">
            <Button label="Close" severity="secondary" outlined rounded className="mt-2 p-button-outlined" onClick={() => setShowUpcomingRemindersPopUp(false)} />
          </div>
        }>
        <div style={appFormSectionStyle}>
          <label htmlFor="upcomingReminderFilter" className="mb-2"><strong>Time Range: </strong></label>
          <Dropdown value={selectedUpcomingReminderFilter} options={UPCOMING_MONTH_DROPDOWN_OPTIONS}
            onChange={(e) => { setSelectedUpcomingReminderFilter(e.value); onUpcomingRemindersClick(e.value); }} placeholder="Select range" style={{ width: '15em', marginBottom: '1em' }} />
          <div style={appDataTableWrapperStyle}>
            <DataTable
              value={upcomingReminders || []}
              emptyMessage="No upcoming reminders."
              showGridlines
              stripedRows
              paginator
              rows={isMobile ? 5 : 10}
              className="p-datatable-sm"
            >
              <Column field="title" header="Title" sortable></Column>
              <Column
                header="Reminder Date"
                sortable
                body={(rowData) => rowData?.dueDate ? new Date(rowData.dueDate).toLocaleDateString() : "-"}
              ></Column>
              <Column field="frequency" header="Frequency" sortable></Column>
              <Column field="earlyWeeks" header="Lead Time" sortable></Column>
            </DataTable>
          </div>
        </div>
      </Dialog>

      <Dialog header={renderSurfaceHeader("Reminder History", "Browse previously recorded reminders and their statuses.", "pi pi-history")} visible={showAllRemindersPopUp} style={appWideDialogStyle} modal onHide={() => setShowAllRemindersPopUp(false)}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        footer={
          <div className="justify-content-center align-items-center flex flex-wrap">
            <Button label="Close" severity="secondary" outlined rounded className="mt-2 p-button-outlined" onClick={() => setShowAllRemindersPopUp(false)} />
          </div>
        }>
        <div style={appFormSectionStyle}>
          <div style={appDataTableWrapperStyle}>
            <DataTable
              value={allRemindersByDate || []}
              emptyMessage="No reminders found."
              showGridlines
              stripedRows
              paginator
              rows={isMobile ? 5 : 10}
              className="p-datatable-sm"
            >
              <Column field="title" header="Title" sortable></Column>
              <Column
                header="Due Date"
                sortable
                body={(rowData) => rowData?.dueDate ? new Date(rowData.dueDate).toLocaleDateString() : "-"}
              ></Column>
              <Column field="status" header="Status" sortable></Column>
              <Column field="frequency" header="Frequency" sortable></Column>
            </DataTable>
          </div>
        </div>
      </Dialog>
    </div >
  );
}

export default UserReminder;
